import { Fragment } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { BuildingSmall, Repeat } from "@acto/web-common/components";
import { SidePanelContainer, SidePanelHeader, SidePanelSection, DateColumn, DatedListDivider } from "../ui";
import { Flex, Chip } from "../../primitives";

interface TodoItem {
  id: string;
  date: string;
  title: string;
  description: string;
  clientId: string | null;
  clientName: string | null;
  clientAccessible: boolean;
}

interface OpenTodosData {
  employeeName: string;
  overdueTodos: {
    count: number;
    items: TodoItem[];
  };
  openTodos: {
    count: number;
    items: TodoItem[];
  };
}

interface OpenTodosSidePanelProps {
  data: OpenTodosData;
  onTodoClick: (clientId: string) => void;
}

const TodoCard = styled("button")(({ theme }) => ({
  display: "flex",
  gap: theme.managementView.spacing[16],
  alignItems: "start",
  transition: "background-color 0.2s",
  textAlign: "left",
  padding: theme.managementView.spacing[12],
  borderRadius: theme.managementView.radius[10],
  marginLeft: `-${theme.managementView.spacing[12]}`,

  "&[data-clickable='true']": {
    cursor: "pointer",

    "&:hover": {
      backgroundColor: theme.managementView.colors.neutral[4],
    },
  },
}));

const TodoTitle = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.managementView.spacing[8],
  width: "100%",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[90],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  flex: 1,
  textAlign: "left",
}));

const TodoDescription = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[90],
  textAlign: "left",
}));

const TodoDetailsList = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: `${theme.managementView.spacing[4]} ${theme.managementView.spacing[12]}`,
  alignItems: "center",
}));

const TodoDetailItem = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[6],
}));

export const OpenTodosSidePanel = ({ data, onTodoClick }: OpenTodosSidePanelProps) => {
  const t = useTranslations();

  const openTodoClient = (todo: { clientId: string | null; clientAccessible: boolean }) => {
    const { clientId } = todo;
    return todo.clientAccessible && clientId != null ? () => onTodoClick(clientId) : undefined;
  };

  return (
    <SidePanelContainer>
      <SidePanelHeader title={t("managementView.openTodos.title")} subtitle={data.employeeName} />

      <SidePanelSection title={`${data.overdueTodos.count} ${t("managementView.openTodos.overdue")}`}>
        <Flex direction="column">
          {data.overdueTodos.items.map((todo, index) => {
            const handleOpenClient = openTodoClient(todo);
            return (
              <Fragment key={todo.id}>
                <TodoCard data-clickable={Boolean(handleOpenClient)} onClick={handleOpenClient}>
                  <DateColumn>
                    <Chip size="large" color="error">
                      {todo.date}
                    </Chip>
                  </DateColumn>
                  <Flex direction="column" align="start" gap={24}>
                    <Flex direction="column" gap={2} align="start" style={{ width: "100%" }}>
                      <TodoTitle>{todo.title}</TodoTitle>
                      <TodoDescription>{todo.description}</TodoDescription>
                    </Flex>
                    <TodoDetailsList>
                      <TodoDetailItem>
                        <Repeat style={{ width: "12px", height: "12px", color: "inherit" }} />
                      </TodoDetailItem>
                      <TodoDetailItem>
                        <BuildingSmall style={{ width: "12px", height: "12px", color: "inherit" }} />
                        <span>{todo.clientName ?? t("managementView.deletedClient")}</span>
                      </TodoDetailItem>
                    </TodoDetailsList>
                  </Flex>
                </TodoCard>
                {(index < data.overdueTodos.items.length - 1 || data.openTodos.items.length > 0) && (
                  <DatedListDivider />
                )}
              </Fragment>
            );
          })}
        </Flex>
      </SidePanelSection>

      <SidePanelSection title={`${data.openTodos.count} ${t("managementView.openTodos.open")}`}>
        <Flex direction="column">
          {data.openTodos.items.map((todo, index) => {
            const handleOpenClient = openTodoClient(todo);
            return (
              <Fragment key={todo.id}>
                <TodoCard data-clickable={Boolean(handleOpenClient)} onClick={handleOpenClient}>
                  <DateColumn>
                    <Chip size="large" color="default">
                      {todo.date}
                    </Chip>
                  </DateColumn>
                  <Flex direction="column" align="start" gap={24}>
                    <Flex direction="column" gap={2} align="start" style={{ width: "100%" }}>
                      <TodoTitle>{todo.title}</TodoTitle>
                      <TodoDescription>{todo.description}</TodoDescription>
                    </Flex>
                    <TodoDetailsList>
                      <TodoDetailItem>
                        <Repeat style={{ width: "12px", height: "12px", color: "inherit" }} />
                      </TodoDetailItem>
                      <TodoDetailItem>
                        <BuildingSmall style={{ width: "12px", height: "12px", color: "inherit" }} />
                        <span>{todo.clientName ?? t("managementView.deletedClient")}</span>
                      </TodoDetailItem>
                    </TodoDetailsList>
                  </Flex>
                </TodoCard>
                {index < data.openTodos.items.length - 1 && <DatedListDivider />}
              </Fragment>
            );
          })}
        </Flex>
      </SidePanelSection>
    </SidePanelContainer>
  );
};
